// 页 2：AI 候选详情（抽屉）
// 10 状态机 + 9 步链路 + 7 表数据 tabs + 操作按钮

import React from 'react';
import { Drawer, Card, Tag, Row, Col, Descriptions, Image, Empty, Button, Space } from 'antd';
import { Tabs } from '../AntdCompat';
import { LifecycleStateBar, EndToEndFlowChart, DecisionAuditTimeline, ImageCandidateGrid, PriceTierCard } from '../components';
import {
  aiCandidates, STATUS_LABELS, STATUS_COLORS, SITE_NAMES,
  getDecisionsByCandidate, getQualitiesByCandidate, getDraftsByCandidate,
  getPriceRecByCandidate, getRefsByCandidate, getEffectsByCandidate,
} from '../data';

interface Props {
  candidateId: string | null;
  onClose: () => void;
}

const CandidateDetail: React.FC<Props> = ({ candidateId, onClose }) => {
  if (!candidateId) return null;
  const c = aiCandidates.find(c => c.candidate_id === candidateId);
  if (!c) return null;

  const decisions = getDecisionsByCandidate(candidateId);
  const qualities = getQualitiesByCandidate(candidateId);
  const drafts = getDraftsByCandidate(candidateId);
  const priceRec = getPriceRecByCandidate(candidateId);
  const refs = getRefsByCandidate(candidateId);
  const effects = getEffectsByCandidate(candidateId);

  return (
    <Drawer
      title={
        <Space>
          <Image src={c.thumbnail_url} width={32} height={32} preview={false} style={{ borderRadius: 4 }} />
          <span>{c.title}</span>
          <Tag color={STATUS_COLORS[c.status]}>{STATUS_LABELS[c.status]}</Tag>
          <span style={{ fontSize: 11, color: '#8c8c8c', fontFamily: 'monospace' }}>{c.candidate_id}</span>
        </Space>
      }
      visible={!!candidateId}
      onClose={onClose}
      width="80%"
      destroyOnClose
      extra={
        <Space>
          <Button>编辑</Button>
          <Button danger>拒绝</Button>
          <Button type="primary">推进下一阶段</Button>
        </Space>
      }
    >
      {/* 顶部：状态机 + 链路 */}
      <Card size="small" title="生命周期状态" style={{ marginBottom: 16 }}>
        <LifecycleStateBar status={c.status} />
      </Card>

      <Card size="small" title="一条候选款端到端 9 步链路" style={{ marginBottom: 16 }}>
        <EndToEndFlowChart status={c.status} />
      </Card>

      {/* 7 表数据 tabs */}
      <Tabs
        defaultActiveKey="basic"
        items={[
          {
            key: 'basic',
            label: '① 基本信息 (ai_candidate)',
            children: (
              <Card size="small">
                <Descriptions size="small" column={2} bordered>
                  <Descriptions.Item label="候选 ID">{c.candidate_id}</Descriptions.Item>
                  <Descriptions.Item label="EPS">{c.eps_id}</Descriptions.Item>
                  <Descriptions.Item label="站点">{SITE_NAMES[c.site_id]}</Descriptions.Item>
                  <Descriptions.Item label="风格">{c.style_id}</Descriptions.Item>
                  <Descriptions.Item label="来源">{c.source_type}</Descriptions.Item>
                  <Descriptions.Item label="来源引用">{c.source_ref_id}</Descriptions.Item>
                  <Descriptions.Item label="批次号">{c.batch_no}</Descriptions.Item>
                  <Descriptions.Item label="负责人">{c.owner_name}</Descriptions.Item>
                  <Descriptions.Item label="AI 模型">{c.created_by_ai_model}</Descriptions.Item>
                  <Descriptions.Item label="prompt 版本">{c.prompt_version}</Descriptions.Item>
                  <Descriptions.Item label="创建时间" span={2}>{new Date(c.created_at).toLocaleString('zh-CN')}</Descriptions.Item>
                  <Descriptions.Item label="最后更新" span={2}>{new Date(c.updated_at).toLocaleString('zh-CN')}</Descriptions.Item>
                  {c.outcome && <Descriptions.Item label="复盘结论" span={2}>
                    <Tag color={c.outcome === 'HIT' ? 'success' : c.outcome === 'MISS' ? 'error' : 'warning'}>{c.outcome}</Tag>
                  </Descriptions.Item>}
                </Descriptions>
              </Card>
            ),
          },
          {
            key: 'refs',
            label: `② 引用关系 (ai_candidate_ref · ${refs.length})`,
            children: (
              <Card size="small">
                <p style={{ color: '#8c8c8c', fontSize: 12 }}>
                  跨库逻辑引用，**不拉强外键**。每条引用带 ref_snapshot 时点快照，避免源对象后续变动失真。
                </p>
                {refs.length === 0 ? <Empty /> : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <th style={{ padding: 6, textAlign: 'left' }}>ref_type</th>
                        <th style={{ padding: 6, textAlign: 'left' }}>ref_id</th>
                        <th style={{ padding: 6, textAlign: 'left' }}>ref_snapshot</th>
                        <th style={{ padding: 6, textAlign: 'left' }}>建立时间</th>
                      </tr>
                    </thead>
                    <tbody>
                      {refs.map(r => (
                        <tr key={r.id} style={{ borderBottom: '1px dashed #f5f5f5' }}>
                          <td style={{ padding: 6 }}><Tag color="cyan">{r.ref_type}</Tag></td>
                          <td style={{ padding: 6, fontFamily: 'monospace' }}>{r.ref_id}</td>
                          <td style={{ padding: 6 }}><code>{JSON.stringify(r.ref_snapshot)}</code></td>
                          <td style={{ padding: 6, color: '#8c8c8c' }}>{new Date(r.created_at).toLocaleString('zh-CN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            ),
          },
          {
            key: 'decisions',
            label: `③ 决策审计 (ai_candidate_decision · ${decisions.length})`,
            children: (
              <Card size="small">
                <p style={{ color: '#8c8c8c', fontSize: 12 }}>所有状态变化、人工 select/reject/edit 都进这张表。不允许无审计变化。</p>
                <DecisionAuditTimeline decisions={decisions} />
              </Card>
            ),
          },
          {
            key: 'quality',
            label: `④ 图候选质量 (ai_candidate_quality · ${qualities.length})`,
            children: (
              <Card size="small">
                <ImageCandidateGrid qualities={qualities} />
              </Card>
            ),
          },
          {
            key: 'listing',
            label: `⑤ listing 草稿 (ai_listing_draft · ${drafts.length})`,
            children: (
              <Card size="small">
                {drafts.length === 0 ? <Empty description="尚未生成 listing 草稿" /> : drafts.map(d => (
                  <Card key={d.id} type="inner" title={
                    <span>v{d.version_no}
                      <Tag color="purple" style={{ marginLeft: 8 }}>{d.model_version}</Tag>
                      <Tag>prompt {d.prompt_version}</Tag>
                      <span style={{ fontSize: 11, color: '#8c8c8c', marginLeft: 8 }}>
                        {new Date(d.generated_at).toLocaleString('zh-CN')}
                      </span>
                    </span>
                  } style={{ marginBottom: 12 }}>
                    <Descriptions size="small" column={1}>
                      <Descriptions.Item label="title">{d.title}</Descriptions.Item>
                      <Descriptions.Item label="handle">{d.handle}</Descriptions.Item>
                      <Descriptions.Item label="seo_title">{d.seo_title}</Descriptions.Item>
                      <Descriptions.Item label="seo_desc">{d.seo_desc}</Descriptions.Item>
                      <Descriptions.Item label="tags">{d.tags.map(t => <Tag key={t}>{t}</Tag>)}</Descriptions.Item>
                      <Descriptions.Item label="collections">{d.collections.map(c => <Tag key={c} color="cyan">{c}</Tag>)}</Descriptions.Item>
                      <Descriptions.Item label="description">
                        <div dangerouslySetInnerHTML={{ __html: d.description_html }} style={{ fontSize: 13 }} />
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ))}
              </Card>
            ),
          },
          {
            key: 'price',
            label: '⑥ 三档价 (ai_price_recommendation)',
            children: priceRec ? <PriceTierCard rec={priceRec} /> : <Empty description="尚未生成定价" />,
          },
          {
            key: 'effect',
            label: `⑦ 效果时序 (ai_candidate_effect_daily · ${effects.length})`,
            children: (
              <Card size="small">
                {effects.length === 0 ? <Empty description="尚未发布或进入观察期" /> : (
                  <>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                      <Col span={4}>
                        <Card size="small"><div>累计 PV</div><div style={{ fontSize: 20, fontWeight: 600 }}>{effects.reduce((s, e) => s + e.pv, 0)}</div></Card>
                      </Col>
                      <Col span={4}>
                        <Card size="small"><div>累计加购</div><div style={{ fontSize: 20, fontWeight: 600, color: '#fa8c16' }}>{effects.reduce((s, e) => s + e.add_to_cart, 0)}</div></Card>
                      </Col>
                      <Col span={4}>
                        <Card size="small"><div>已购</div><div style={{ fontSize: 20, fontWeight: 600, color: '#52c41a' }}>{effects.reduce((s, e) => s + e.purchased, 0)}</div></Card>
                      </Col>
                      <Col span={4}>
                        <Card size="small"><div>询单</div><div style={{ fontSize: 20, fontWeight: 600 }}>{effects.reduce((s, e) => s + e.inquiry_opened, 0)}</div></Card>
                      </Col>
                      <Col span={4}>
                        <Card size="small"><div>退货</div><div style={{ fontSize: 20, fontWeight: 600, color: '#cf1322' }}>{effects.reduce((s, e) => s + e.returned, 0)}</div></Card>
                      </Col>
                    </Row>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <th style={{ padding: 4, textAlign: 'left' }}>日期</th>
                          <th style={{ padding: 4 }}>PV</th>
                          <th style={{ padding: 4 }}>UV</th>
                          <th style={{ padding: 4 }}>加购</th>
                          <th style={{ padding: 4 }}>结账</th>
                          <th style={{ padding: 4 }}>购</th>
                          <th style={{ padding: 4 }}>询</th>
                          <th style={{ padding: 4 }}>退</th>
                        </tr>
                      </thead>
                      <tbody>
                        {effects.slice().reverse().slice(0, 14).map(e => (
                          <tr key={e.id} style={{ borderBottom: '1px dashed #f5f5f5' }}>
                            <td style={{ padding: 4 }}>{e.effect_date.split('T')[0]}</td>
                            <td style={{ padding: 4, textAlign: 'center' }}>{e.pv}</td>
                            <td style={{ padding: 4, textAlign: 'center' }}>{e.unique_visitor}</td>
                            <td style={{ padding: 4, textAlign: 'center', color: '#fa8c16' }}>{e.add_to_cart}</td>
                            <td style={{ padding: 4, textAlign: 'center' }}>{e.checkout_start}</td>
                            <td style={{ padding: 4, textAlign: 'center', color: '#52c41a' }}>{e.purchased}</td>
                            <td style={{ padding: 4, textAlign: 'center' }}>{e.inquiry_opened}</td>
                            <td style={{ padding: 4, textAlign: 'center', color: '#cf1322' }}>{e.returned}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </Card>
            ),
          },
        ]}
      />
    </Drawer>
  );
};

export default CandidateDetail;
